import { TextInputDropdownViewState } from '../components/convert-options-v2/convert-options.view-states';

/**
 * FIX-8 (UX-SPEC.md section 8.1): is the run the user is about to start the SAME as one they
 * already started? Pure, so the whole equality matrix is unit-tested without a page.
 *
 * WHY STRICT. A false "unchanged" would get in the way of a run the user genuinely wants, so
 * every rule below errs towards "changed": any difference in any posted option, any missing
 * fingerprint part, any unknown card state means "run normally". The only things treated alike
 * are the shapes that reach the engine as the same request:
 *   - absent, `''` and an empty `value::unit` (`'::SECONDS'`) -- the normalisation ac1e532f1 made
 *     for the posted summary (`ConvertOptionsContextV2.getSummary`), through the SAME predicate
 *     (`TextInputDropdownViewState.isEmptyValue`), applied to every key here because a card saved
 *     before ac1e532f1 still carries the old `''` shape;
 *   - absent and the page's default (defaults filled in);
 *   - `"4::mbps"` and `" 4.0 :: mbps"` (value::unit normalised: trimmed, the number canonical);
 *   - keys sorted, and the keys in {@link ENGINE_IGNORED_OPTION_KEYS}, which never reach the engine.
 * Trim, frame time, output name, orientation and merge order are ordinary posted options (or the
 * input order) and so always count.
 *
 * Client-side only, by design (section 8.6): no server dedupe, nothing for the public API or for
 * automation workspaces. The schedule call's own duplicate protection -- the `Idempotency-Key`
 * replay counted as `schedule.dedup_hit` (services/scheduling/idempotency-key.ts) -- is left
 * exactly as it is and still covers the automatic retries it was built for; this module never
 * touches a key or a nonce.
 */

/**
 * Keys a schedule call posts that the engine never reads, so they must not make two runs differ.
 * Keep this list next to the normaliser and SHORT: a key wrongly listed here turns a real change
 * into "unchanged". Each entry carries its evidence.
 *   - `uploadedFileExtensions`: a root property the tool page writes from the staged files'
 *     extensions to drive the options tree (`file-convert.component.tsx` updateUploadedFileExtensions);
 *     no reader in ironman, professorx or the shared library (grep, 2026-10-02). The Re-edit
 *     modal never writes it, so it would otherwise make every tool-page run differ from a re-edit.
 */
export const ENGINE_IGNORED_OPTION_KEYS: readonly string[] = ['uploadedFileExtensions'];

type Canon = string | Canon[] | { [key: string]: Canon };

const VALUE_UNIT = /^([^:]*)::([^:]*)$/;
const PLAIN_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)$/;

/** Absent, `''` and an empty `value::unit` are one thing: nothing posted. */
export function isEmptyOptionValue(value: unknown): boolean {
    if (TextInputDropdownViewState.isEmptyValue(value)) return true;
    if (typeof value === 'string') {
        const m = value.match(VALUE_UNIT);
        if (m && TextInputDropdownViewState.isEmptyValue(m[1].trim())) return true;
    }
    return false;
}

function canonValueUnit(value: string): string {
    const m = value.match(VALUE_UNIT);
    if (!m) return value;
    const v = m[1].trim();
    const unit = m[2].trim();
    const num = PLAIN_NUMBER.test(v) ? String(Number(v)) : v;
    return `${num}::${unit}`;
}

function canon(value: unknown): Canon | undefined {
    if (isEmptyOptionValue(value)) return undefined;
    if (Array.isArray(value)) {
        // Order is meaningful (orientation per input, merge order): positions are kept, an empty
        // slot stays an empty slot rather than shifting the rest.
        return value.map(v => canon(v) ?? '');
    }
    if (typeof value === 'object') {
        const out: { [key: string]: Canon } = {};
        Object.keys(value as object).sort().forEach(k => {
            const c = canon((value as any)[k]);
            if (c !== undefined) out[k] = c;
        });
        return out;
    }
    if (typeof value === 'string') return canonValueUnit(value);
    // 75 and "75", true and "true": the request is JSON and the engine reads strings.
    return String(value);
}

/**
 * The canonical form of the options a schedule call would post: sorted keys, empties dropped,
 * absent filled from `defaults`, engine-ignored keys dropped, value::unit normalised.
 */
export function canonicalOptions(
    options: Record<string, unknown> | null | undefined,
    defaults?: Record<string, unknown> | null,
): Record<string, Canon> {
    const keys = new Set<string>([...Object.keys(options || {}), ...Object.keys(defaults || {})]);
    const out: Record<string, Canon> = {};
    Array.from(keys).sort().forEach(key => {
        if (ENGINE_IGNORED_OPTION_KEYS.includes(key)) return;
        const own = options ? options[key] : undefined;
        const value = isEmptyOptionValue(own) ? (defaults ? defaults[key] : undefined) : own;
        const c = canon(value);
        if (c !== undefined) out[key] = c;
    });
    return out;
}

/** A stable string for {@link canonicalOptions}; equal strings mean the same posted options. */
export function canonicalOptionsKey(
    options: Record<string, unknown> | null | undefined,
    defaults?: Record<string, unknown> | null,
): string {
    return JSON.stringify(canonicalOptions(options, defaults));
}

// ------------------------------------------------------------------------------------ the source

/**
 * name + size + lastModified of a picked file (section 8.1 item 1, fresh upload). Null when any
 * part is missing or unusable -- a cloud pick, a restored row with `lastModified: 0` -- and null
 * never matches anything.
 */
export function fileFingerprint(file: { name?: unknown; size?: unknown; lastModified?: unknown } | null | undefined): string | null {
    if (!file) return null;
    const name = typeof file.name === 'string' ? file.name : '';
    const size = Number(file.size);
    const modified = Number(file.lastModified);
    if (!name || !Number.isFinite(size) || size < 0 || !Number.isFinite(modified) || modified <= 0) return null;
    return `${name}\u0000${size}\u0000${modified}`;
}

/** The same fingerprint, read off a card's inputs (`OriginalInputFile.metadata`, file-upload.service.ts). */
export function cardInputFingerprints(card: any): (string | null)[] {
    const inputs: any[] = Array.isArray(card?.originalInputFiles) ? card.originalInputFiles : [];
    return inputs.map(input => fileFingerprint({
        name: input?.metadata?.fname,
        size: input?.metadata?.fsize,
        lastModified: input?.metadata?.flastModified,
    }));
}

/** The upload ids a card's run used (`OriginalInputFile.location`, the edge's safe name), in order. */
export function cardUploadIds(card: any): (string | null)[] {
    const inputs: any[] = Array.isArray(card?.originalInputFiles) ? card.originalInputFiles : [];
    return inputs.map(input => (typeof input?.location === 'string' && input.location ? input.location : null));
}

function sameList(a: (string | null)[], b: (string | null)[]): boolean {
    return a.length > 0 && a.length === b.length && a.every((x, i) => x !== null && x === b[i]);
}

// ------------------------------------------------------------------------------ the prior run

export type PriorRunState = 'running' | 'succeeded' | 'other';

export interface PriorRunContext {
    now: number;
    /**
     * How recent a run must be for us to trust what the card says without asking the edge: the
     * FIX-2 `uploadCheckAfterMinutes` (60) -- past it a file may be gone, and an offer to download
     * a result that is gone is exactly what section 8.6 forbids. A run that started earlier and
     * still shows as running is more likely a dead card than a live job, so it is not "running".
     */
    freshMinutes: number;
    /** The hands-off scheduler's view of a card (`queued-card-state.ts`), when it has one. */
    queuedStatus?: (cardId: string) => string | null | undefined;
}

function timeOf(...values: unknown[]): number | null {
    for (const v of values) {
        if (v === undefined || v === null || v === '') continue;
        const t = v instanceof Date ? v.getTime() : new Date(v as any).getTime();
        if (Number.isFinite(t)) return t;
    }
    return null;
}

/**
 * What a card says about its run: in flight (running or queued), finished with its output still
 * downloadable, or anything else (failed, stopped, stalled, too old, unknown) -- which runs
 * normally (section 8.5).
 */
export function priorRunState(card: any, ctx: PriorRunContext): PriorRunState {
    if (!card || card.permanentlyDeleted) return 'other';
    const ws = card.ws || {};
    const result = card.result || null;
    const failed = card.status === 'failed' || result?.error === true || ws.processStageSubState === 'failed';
    if (failed) return 'other';
    const freshMs = Math.max(0, ctx.freshMinutes) * 60_000;
    const hasOutput = !!result && (Array.isArray(result.output) ? result.output.length > 0 : !!result.output);
    if (hasOutput) {
        const ended = timeOf(card.processEndTime, result.end);
        if (ended === null || ctx.now - ended > freshMs || ctx.now < ended - 60_000) return 'other';
        return 'succeeded';
    }
    if (result && Object.keys(result).length > 0) return 'other';
    if (ws.processStage === 'completed') return 'other';
    const queued = ctx.queuedStatus?.(String(card.id ?? '')) ?? null;
    if (queued === 'stalled' || queued === 'retry-all-available') return 'other';
    const started = !!card.workflowId || !!ws.processStage || queued === 'queued' || queued === 'retrying';
    if (!started) return 'other';
    const since = timeOf(card.processStartTime, card.uploadStartTime);
    if (since === null || ctx.now - since > freshMs) return 'other';
    return 'running';
}

// ------------------------------------------------------------------------------- the comparison

/** One schedule request the user is about to make (what `FileProcessEditorComponent` would post). */
export interface PendingRun {
    uniqueProcessType?: string | null;
    toType?: string | null;
    /** Upload ids in input order (Re-edit), null where unknown. */
    uploadIds: (string | null)[];
    /** Input fingerprints in input order (fresh upload), null where unknown. */
    fingerprints: (string | null)[];
    options: Record<string, unknown> | null | undefined;
}

export type SourceMatch = 'uploadIds' | 'fingerprint';

/** Section 8.1, items 1-3, all of them: same source, same tool, same canonical options. */
export function isSameRun(
    prior: any,
    next: PendingRun,
    by: SourceMatch,
    defaults?: Record<string, unknown> | null,
): boolean {
    if (!prior || !next) return false;
    const priorTool = `${prior.uniqueProcessType ?? ''}`;
    if (!priorTool || priorTool !== `${next.uniqueProcessType ?? ''}`) return false;
    if (`${prior.toType ?? ''}`.toLowerCase() !== `${next.toType ?? ''}`.toLowerCase()) return false;
    const source = by === 'uploadIds'
        ? sameList(cardUploadIds(prior), next.uploadIds)
        : sameList(cardInputFingerprints(prior), next.fingerprints);
    if (!source) return false;
    return canonicalOptionsKey(prior.options, defaults) === canonicalOptionsKey(next.options, defaults);
}

export interface UnchangedMatch {
    state: 'running' | 'succeeded';
    /** The prior card per pending run, in the same order. */
    priors: any[];
}

/**
 * Does EVERY pending run repeat a card that is still running, or one that succeeded with its
 * output still there? A mixed batch -- one file new, one changed, one running and one done --
 * answers null and converts without a word (section 8.4).
 */
export function findUnchanged(
    pending: PendingRun[],
    cards: any[] | null | undefined,
    by: SourceMatch,
    ctx: PriorRunContext,
    defaults?: Record<string, unknown> | null,
): UnchangedMatch | null {
    if (!pending || pending.length === 0 || !cards || cards.length === 0) return null;
    const newestFirst = cards.filter(Boolean).slice().sort((a, b) =>
        (timeOf(b.processStartTime, b.uploadStartTime) ?? 0) - (timeOf(a.processStartTime, a.uploadStartTime) ?? 0));
    const states: PriorRunState[] = [];
    const priors: any[] = [];
    for (const run of pending) {
        // The NEWEST matching card decides: if the latest identical run failed, stopped or lost its
        // output, the user is retrying it and it runs normally (section 8.5), even when an older
        // identical run once succeeded.
        const latest = newestFirst.find(card => isSameRun(card, run, by, defaults));
        if (!latest) return null;
        const state = priorRunState(latest, ctx);
        if (state === 'other') return null;
        states.push(state);
        priors.push(latest);
    }
    if (states.every(s => s === 'running')) return { state: 'running', priors };
    if (states.every(s => s === 'succeeded')) return { state: 'succeeded', priors };
    return null;
}
